/**
 * The modal both administration dialogs use — screens 07 and 10 drew the same one twice.
 *
 * Ported from the mockups' markup: a gradient band header with the title in 11px bold
 * uppercase, the body scrolling inside a 32rem card, the page behind it blurred.
 *
 * ⚠ Escape closes it and the backdrop closes it, neither of which the mockup did. A dialog
 * that can only be dismissed by finding the small × is the one interaction people report
 * as "it froze". The click handler checks the TARGET is the backdrop itself: without that,
 * releasing a text selection outside the card would count as a click on it and throw away
 * a half-filled form.
 */

import { useEffect, type ReactNode } from "react";

import { X } from "lucide-react";

/**
 * How wide the card may grow. `report` fits a table of seventeen columns — the contractors'
 * work, month by month (feature 29) — which the 42rem of `wide` cannot hold.
 */
const MAX_WIDTH = { normal: "max-w-lg", wide: "max-w-2xl", report: "max-w-6xl" } as const;

export function Dialog({
  title,
  onClose,
  children,
  size = "normal",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  size?: keyof typeof MAX_WIDTH;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // The page behind must not scroll under the modal — on a long user list it looks like
    // the dialog is sliding away.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  // ⚠ Centred by the card's AUTO margins, never by `items-center` on the backdrop. A card taller
  // than the screen, centred by the flex container, overflows at the top as much as at the
  // bottom — and the top of an overflow container cannot be scrolled to: the title and the
  // first rows were simply out of reach (the contractors' dialog, 29 Sep 2026, on a laptop).
  // Auto margins centre it when it fits and collapse to zero when it does not, so a tall card
  // starts at the top and scrolls whole.
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm sm:py-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className={`my-auto w-full rounded-xl bg-white shadow-2xl ${MAX_WIDTH[size]}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="gradient-band flex items-center justify-between rounded-t-xl px-6 py-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-white">{title}</h3>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * One of two or more cards you pick between — the mockups' `.auth-pick` / `.kind-pick`,
 * which were the same six declarations under two names and are now `.pick-card` in
 * `theme.css`.
 */
export function PickCard({
  selected,
  onSelect,
  title,
  description,
  disabled = false,
  disabledReason,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      className={`pick-card flex items-start gap-3 text-left${selected ? " sel" : ""}${disabled ? " opacity-50" : ""}`}
    >
      <span
        className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
        style={{ borderColor: selected ? "#ed125f" : "#cbd5e1" }}
      >
        {selected && <span className="h-2 w-2 rounded-full" style={{ background: "#ed125f" }} />}
      </span>
      <span>
        <span className="block text-sm font-semibold text-slate-800">{title}</span>
        <span className="mt-0.5 block text-xs text-slate-500">{description}</span>
      </span>
    </button>
  );
}
